import { vec3 } from 'gl-matrix';
import { Engine } from '../Engine';
import { AttachedCamera, Camera } from '../camera';
import { InputHandler } from '../input';
import { Scene } from '../scene/Scene';
import { RenderObject } from '../object/RenderObject';
import { NavigationAwareObject } from '../object/NavigationAwareObject';
import { InputControlledBehaviour } from '../object/behaviours/InputControlledBehaviour';
import { EntityMovementBehaviour } from '../object/behaviours/EntityMovementBehaviour';
import { PlayerControlBehaviour } from '../object/behaviours/PlayerControlBehaviour';

export interface PointerLockProvider {
  requestLock(): void;
  exitLock(): void;
  isLocked(): boolean;
  onMouseDelta?: (dx: number, dy: number) => void;
}

type ActionList = { [key: string]: () => void };

export type PlayerControllerConfig = {
  engine: Engine;
  inputHandler: InputHandler;
  playerCamera: AttachedCamera;
  freeCamera: Camera;
  cameraActions: ActionList;
  playerActions: ActionList;
  pointerLock: PointerLockProvider;
  mouseSensitivity?: number;
  desiredCameraDistance?: number;
  onAttack?: () => void;
};

export class PlayerController {
  public controlMode: 'free-camera' | 'player' = 'free-camera';

  private engine: Engine;
  private inputHandler: InputHandler;
  private playerCamera: AttachedCamera;
  private freeCamera: Camera;
  private cameraActions: ActionList;
  private playerActions: ActionList;
  private pointerLock: PointerLockProvider;
  private mouseSensitivity: number;
  private desiredCameraDistance: number;
  private onAttack?: () => void;

  private playerControlBehaviour?: PlayerControlBehaviour;
  private moveBehaviour?: EntityMovementBehaviour;
  public navObject?: NavigationAwareObject;
  private scene?: Scene;
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private shiftHeld = false;

  private keydownListener?: (e: KeyboardEvent) => void;
  private keyupListener?: (e: KeyboardEvent) => void;

  constructor(config: PlayerControllerConfig) {
    this.engine = config.engine;
    this.inputHandler = config.inputHandler;
    this.playerCamera = config.playerCamera;
    this.freeCamera = config.freeCamera;
    this.cameraActions = config.cameraActions;
    this.playerActions = config.playerActions;
    this.pointerLock = config.pointerLock;
    this.mouseSensitivity = config.mouseSensitivity ?? 0.15;
    this.desiredCameraDistance = config.desiredCameraDistance ?? 4;
    this.onAttack = config.onAttack;

    // Wire up mouse delta accumulation
    this.pointerLock.onMouseDelta = (dx, dy) => {
      this.mouseDeltaX += dx;
      this.mouseDeltaY += dy;
    };
  }

  setupEntity(scene: Scene, playerObject: RenderObject): void {
    this.scene = scene;
    const navObject = scene.createNavigationAwareObject(playerObject, 2);
    this.navObject = navObject;

    navObject.bind(new InputControlledBehaviour(scene));
    const moveBehaviour = new EntityMovementBehaviour(playerObject);
    navObject.bind(moveBehaviour);
    this.moveBehaviour = moveBehaviour;

    const playerControlBehaviour = new PlayerControlBehaviour(playerObject, {
      walkSpeed: 2,
      runSpeed: 4,
      getCameraYaw: () => this.playerCamera.rotation,
      getInputState: () => ({
        forward: !!this.inputHandler.keyState['KeyW'],
        backward: !!this.inputHandler.keyState['KeyS'],
        left: !!this.inputHandler.keyState['KeyA'],
        right: !!this.inputHandler.keyState['KeyD'],
        running: this.shiftHeld,
      }),
    });
    this.playerControlBehaviour = playerControlBehaviour;

    // Look at head level, derived from entity bounding box height (scaled to world space)
    const rawHeight = playerObject.bounds?.maxDimensions?.[1] ?? 4;
    const entityHeight = rawHeight * (playerObject.trs?.scale ?? 1);
    this.playerCamera.targetYOffset = entityHeight * 0.85;

    this.playerCamera.attach({
      get position() {
        return playerObject.position as any;
      },
      get rotation() {
        // Recover yaw from the rotation quat — atan2 of the +Z forward in
        // the XZ plane is gimbal-lock-safe, unlike asin-decomposing rotationY.
        const forward = vec3.transformQuat(
          vec3.create(),
          [0, 0, 1],
          playerObject.trs.getRotationQuat(),
        );
        return (Math.atan2(forward[0], forward[2]) * 180) / Math.PI;
      },
    });

    // Shift and action key listeners
    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        this.shiftHeld = true;
        moveBehaviour.running = true;
      }
      if (!playerObject.animationState) return;
      if (e.key === 'x' || e.key === 'X') {
        playerObject.animationState.switchState('Attack');
        this.onAttack?.();
      } else if (e.key === 'r' || e.key === 'R') {
        playerObject.animationState.switchState('Death');
      }
    };
    this.keyupListener = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        this.shiftHeld = false;
        moveBehaviour.running = false;
      }
    };
    document.addEventListener('keydown', this.keydownListener);
    document.addEventListener('keyup', this.keyupListener);
  }

  registerFrameEvents(scene: Scene): void {
    // Step player controller
    scene.tickEventManager.registerFrameEvent(() => {
      if (this.controlMode === 'player') {
        this.playerControlBehaviour?.step();
      }
    });

    // Apply mouse deltas and constrain camera
    scene.tickEventManager.registerFrameEvent(() => {
      if (this.controlMode === 'player' && this.pointerLock.isLocked()) {
        this.playerCamera.rotation -= this.mouseDeltaX * this.mouseSensitivity;
        this.playerCamera.tilt -= this.mouseDeltaY * this.mouseSensitivity;
      }
      this.mouseDeltaX = 0;
      this.mouseDeltaY = 0;

      if (this.controlMode === 'player' && this.navObject) {
        this.constrainCameraDistance();
      }
    });
  }

  toggleControlMode(): void {
    this.setControlMode(this.controlMode === 'free-camera' ? 'player' : 'free-camera');
  }

  setControlMode(mode: 'free-camera' | 'player'): void {
    if (mode === this.controlMode) return;

    if (mode === 'player') {
      this.controlMode = 'player';
      this.inputHandler.setInputActions(this.playerActions);
      this.engine.activeCamera = this.playerCamera;
      this.playerCamera.freeOrbit = true;

      if (this.navObject && this.playerControlBehaviour) {
        this.navObject.movement.clear();
        this.navObject.bind(this.playerControlBehaviour);
      }

      this.pointerLock.requestLock();
    } else {
      this.controlMode = 'free-camera';
      this.inputHandler.setInputActions(this.cameraActions);
      this.engine.activeCamera = this.freeCamera;
      this.playerCamera.freeOrbit = false;

      if (this.navObject && this.playerControlBehaviour) {
        this.navObject.unbind(this.playerControlBehaviour);
      }

      this.pointerLock.exitLock();
    }
  }

  teardown(): void {
    if (this.controlMode === 'player') {
      this.setControlMode('free-camera');
    }
    this.navObject = undefined;
    this.playerControlBehaviour = undefined;
    this.moveBehaviour = undefined;

    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = undefined;
    }
    if (this.keyupListener) {
      document.removeEventListener('keyup', this.keyupListener);
      this.keyupListener = undefined;
    }
  }

  private constrainCameraDistance(): void {
    const cam = this.playerCamera;
    const target = cam.targetPosition;
    if (!target) return;

    const grid = this.scene?.spatialGrid;
    if (!grid) return;

    const rotation = cam.rotation - 180;
    const tiltRad = (cam.tilt * Math.PI) / 180;
    const cosTilt = Math.cos(tiltRad);
    const sinTilt = Math.sin(tiltRad);
    const sinR = Math.sin((rotation * Math.PI) / 180);
    const cosR = Math.cos((rotation * Math.PI) / 180);

    const targetX = target[0] as number;
    const targetY = target[1] as number;
    const targetZ = target[2] as number;
    const minClearance = 0.3;

    // Find lowest ceiling along the probe path, queried at camera height so
    // ground-level terrain bumps don't register as ceilings.
    const ceilingProbeY = targetY + cam.targetYOffset + cam.yOffset;
    let minCeiling: number | null = null;
    const steps = 8;
    let validDistance = 0.4;
    for (let i = 0; i <= steps; i++) {
      const d = (this.desiredCameraDistance * i) / steps;
      const horizontalDist = d * cosTilt;
      const probeX = targetX + horizontalDist * sinR;
      const probeZ = targetZ + horizontalDist * cosR;

      const ceiling = grid.getCeilingHeightAbove(probeX, ceilingProbeY, probeZ);
      if (ceiling !== null && (minCeiling === null || ceiling < minCeiling)) {
        minCeiling = ceiling;
      }
    }

    // Clamp camera Y to stay under the lowest overhead ceiling
    const targetMaxY = minCeiling !== null ? minCeiling - minClearance : null;
    if (targetMaxY !== null) {
      cam.maxCameraY =
        cam.maxCameraY !== null
          ? cam.maxCameraY + (targetMaxY - cam.maxCameraY) * 0.15
          : targetMaxY;
    } else {
      cam.maxCameraY = null;
    }

    // Constrain distance for walls and floors (not ceilings — handled above)
    for (let i = 1; i <= steps; i++) {
      const d = (this.desiredCameraDistance * i) / steps;

      const horizontalDist = d * cosTilt;
      const probeX = targetX + horizontalDist * sinR;
      const probeZ = targetZ + horizontalDist * cosR;
      let cameraY = targetY + cam.targetYOffset + cam.yOffset + d * sinTilt;
      if (targetMaxY !== null && cameraY > targetMaxY) cameraY = targetMaxY;

      const floorHeight = grid.getFloorHeight(probeX, cameraY, probeZ);
      if (floorHeight !== null && cameraY < floorHeight + minClearance) break;

      validDistance = d;
    }

    const targetDist = Math.min(validDistance, this.desiredCameraDistance);
    cam.distance += (targetDist - cam.distance) * 0.15;
  }
}
